import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, cant, hoy, primeroDelMes } from '../utils/formato';

// Qué dejó el negocio.
//
// Los materiales y las instalaciones van separados a propósito: son dos
// negocios con márgenes muy distintos, y mezclarlos esconde cuál de los dos
// conviene. En obra casi todo lo que se cobra es ganancia; en materiales hay
// que descontar lo que costó la mercancía.
function Ganancias() {
  const [datos, setDatos] = useState(null);
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setCargando(true); setError('');
    axios.get(`/api/ganancias?desde=${desde}&hasta=${hasta}`)
      .then(r => setDatos(r.data))
      .catch(() => setError('No se pudieron cargar las ganancias'))
      .finally(() => setCargando(false));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  const atajo = (meses) => {
    const d = new Date();
    d.setMonth(d.getMonth() - meses);
    d.setDate(1);
    setDesde(d.toISOString().split('T')[0]);
    setHasta(hoy());
  };

  const r = datos?.resumen || {};
  const caja = datos?.caja || {};
  const gananciaMateriales = (r.materiales || 0) - (r.costo || 0);
  // La mano de obra de las instalaciones es costo de la obra, no gasto del mes.
  // Restarla aquí es lo que hace que instalar deje de verse como si dejara el
  // 100% y se pueda comparar de verdad contra vender materiales.
  const costoObra = r.costo_instalacion || 0;
  const gananciaInstalacion = (r.instalacion || 0) - costoObra;
  const gananciaTotal = (r.total || 0) - (r.costo || 0) - costoObra;
  const gastos = datos?.gastos?.v || 0;
  const nomina = datos?.nomina?.v || 0;
  const queda = gananciaTotal - gastos - nomina;

  const margen = (venta, ganancia) => venta > 0 ? (ganancia / venta * 100) : null;

  return (
    <>
      <div className="encabezado">
        <h1>Ganancias</h1>
        <p>Qué dejó el negocio, y de dónde salió</p>
      </div>

      {error && <div className="aviso malo">{error}</div>}

      <div className="bloque">
        <h2>📅 Periodo</h2>
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
          <div className="acciones">
            <button className="boton suave chico"
                    onClick={() => { setDesde(hoy()); setHasta(hoy()); }}>Hoy</button>
            <button className="boton suave chico"
                    onClick={() => { setDesde(primeroDelMes()); setHasta(hoy()); }}>Este mes</button>
            <button className="boton suave chico" onClick={() => atajo(2)}>3 meses</button>
            <button className="boton suave chico" onClick={() => atajo(11)}>Último año</button>
          </div>
        </div>
      </div>

      {cargando ? (
        <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>
      ) : !datos ? null : (
        <>
          <div className="rejilla">
            <div className="dato" style={{ '--color': 'var(--venta)' }}>
              <div className="rotulo">🛒 Vendido</div>
              <div className="valor cifra">{plata(r.total)}</div>
              <div className="pie">{r.ventas} {r.ventas === 1 ? 'venta' : 'ventas'}</div>
            </div>
            <div className="dato" style={{ '--color': 'var(--acento)' }}>
              <div className="rotulo">💰 Ganancia bruta</div>
              <div className="valor cifra">{plata(gananciaTotal)}</div>
              <div className="pie">
                {margen(r.total, gananciaTotal) != null
                  ? `${margen(r.total, gananciaTotal).toFixed(0)}% de lo vendido` : '—'}
              </div>
            </div>
            <div className="dato" style={{ '--color': 'var(--malo)' }}>
              <div className="rotulo">💸 Gastos y nómina</div>
              <div className="valor cifra">{plata(gastos + nomina)}</div>
              <div className="pie">
                {nomina > 0 ? `${plata(gastos)} gastos · ${plata(nomina)} nómina` : 'del periodo'}
              </div>
            </div>
            <div className="dato" style={{ '--color': queda >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
              <div className="rotulo">{queda >= 0 ? '✅ Quedó' : '⚠️ Faltó'}</div>
              <div className="valor cifra">{plata(Math.abs(queda))}</div>
              <div className="pie">después de todo</div>
            </div>
          </div>

          {/* Los dos negocios, separados */}
          <div className="bloque">
            <h2>🔩 Materiales contra 🔧 instalaciones</h2>
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th></th>
                    <th style={{ textAlign: 'right' }}>Se cobró</th>
                    <th style={{ textAlign: 'right' }}>Costó</th>
                    <th style={{ textAlign: 'right' }}>Dejó</th>
                    <th style={{ textAlign: 'right' }}>Margen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="recio">🔩 Materiales</td>
                    <td className="num">{plata(r.materiales)}</td>
                    <td className="num">{plata(r.costo)}</td>
                    <td className="num recio">{plata(gananciaMateriales)}</td>
                    <td className="num">
                      {margen(r.materiales, gananciaMateriales) != null
                        ? `${margen(r.materiales, gananciaMateriales).toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className="recio">🔧 Instalaciones</td>
                    <td className="num">{plata(r.instalacion)}</td>
                    <td className="num">
                      {costoObra > 0 ? plata(costoObra)
                        : <span style={{ color: 'var(--tinta-tenue)' }}>—</span>}
                      {costoObra > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                          mano de obra
                        </div>
                      )}
                    </td>
                    <td className="num recio">{plata(gananciaInstalacion)}</td>
                    <td className="num">
                      {margen(r.instalacion, gananciaInstalacion) != null
                        ? `${margen(r.instalacion, gananciaInstalacion).toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className="recio">Total</td>
                    <td className="num recio">{plata(r.total)}</td>
                    <td className="num recio">{plata((r.costo || 0) + costoObra)}</td>
                    <td className="num recio" style={{ color: 'var(--acento)' }}>
                      {plata(gananciaTotal)}
                    </td>
                    <td className="num recio">
                      {margen(r.total, gananciaTotal) != null
                        ? `${margen(r.total, gananciaTotal).toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="cuerpo" style={{ paddingTop: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                La instalación no lleva mercancía: su costo es lo que le pagas al
                instalador en cada obra. Si la haces tú, no tiene costo y deja el 100%.
                Van separados porque son dos negocios con márgenes distintos, y
                mezclarlos haría creer que vender tornillos deja más de lo que deja.
              </p>
              {r.instalacion > 0 && costoObra === 0 && (
                <div className="aviso ojo" style={{ marginTop: 10, marginBottom: 0 }}>
                  Ninguna instalación del periodo tiene anotado lo que le pagaste al
                  instalador. Si le pagaste a alguien, el margen de arriba está inflado:
                  anótalo en la venta, en la casilla «cuánto le pago al instalador».
                </div>
              )}
            </div>
          </div>

          {/* De dónde entró la plata: lo que él pidió expresamente */}
          <div className="bloque">
            <h2>💵 Cómo entró la plata</h2>
            <div className="tabla-caja">
              <table className="datos">
                <tbody>
                  <tr>
                    <td className="recio">💵 Efectivo</td>
                    <td className="num recio" style={{ color: 'var(--bueno)' }}>
                      {plata(caja.efectivo)}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                        debería estar en la caja
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="recio">🏦 Transferencia</td>
                    <td className="num recio" style={{ color: 'var(--acero)' }}>
                      {plata(caja.transferencia)}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                        debería estar en el banco
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="recio">📝 Fiado</td>
                    <td className="num recio" style={{ color: 'var(--ojo)' }}>
                      {plata(caja.credito)}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                        todavía en la calle
                      </div>
                    </td>
                  </tr>
                  {datos.por_cobrar?.v > 0 && (
                    <tr>
                      <td className="recio">Le deben en total</td>
                      <td className="num recio">
                        {plata(datos.por_cobrar.v)}
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          de siempre, no solo del periodo
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {datos.por_producto?.length > 0 && (
            <div className="bloque">
              <h2>🏆 Lo que más dejó</h2>
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ textAlign: 'right' }}>Vendidas</th>
                      <th style={{ textAlign: 'right' }}>Se cobró</th>
                      <th style={{ textAlign: 'right' }}>Costó</th>
                      <th style={{ textAlign: 'right' }}>Dejó</th>
                      <th style={{ textAlign: 'right' }}>Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.por_producto.map(p => (
                      <tr key={p.id}>
                        <td className="recio">
                          {p.nombre}
                          <div className="cifra" style={{ fontSize: 11, fontWeight: 400,
                                                          color: 'var(--tinta-tenue)' }}>
                            {p.codigo}
                          </div>
                        </td>
                        <td className="num">{cant(p.vendidas, p.unidad)}</td>
                        <td className="num">{plata(p.vendido)}</td>
                        <td className="num">{plata(p.costo)}</td>
                        <td className="num recio"
                            style={{ color: p.ganancia >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
                          {plata(p.ganancia)}
                        </td>
                        <td className="num">
                          {p.vendido > 0 ? `${(p.ganancia / p.vendido * 100).toFixed(0)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="cuerpo" style={{ paddingTop: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                  Ordenado por lo que DEJÓ, no por lo que se vendió. A veces el producto
                  del que más se vende no es el que más deja, y saber cuál es cambia en
                  qué conviene tener la plata.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default Ganancias;
